import React from 'react';
import { Chip, ChipProps } from '@mui/material';
import {
  CheckCircle,
  Error as ErrorIcon,
  Help as HelpIcon,
  Schedule,
  CheckCircleOutline,
  Visibility,
  OpenInNew,
} from '@mui/icons-material';
import { getServiceStatusMuiColor, type ServiceStatus, type AlertStatus } from './utils/colorUtils';

/**
 * Configuration for status display
 */
interface StatusConfig {
  color: ChipProps['color'];
  icon: React.ReactElement;
  label: string;
}

/**
 * Service status configurations (up/down/unknown)
 */
const SERVICE_STATUS_CONFIG: Record<ServiceStatus, StatusConfig> = {
  up: { color: 'success', icon: <CheckCircle />, label: 'Up' },
  down: { color: 'error', icon: <ErrorIcon />, label: 'Down' },
  unknown: { color: 'default', icon: <HelpIcon />, label: 'Unknown' },
};

/**
 * Alert status configurations (new/acknowledged/dismissed/promoted)
 */
const ALERT_STATUS_CONFIG: Record<AlertStatus, StatusConfig> = {
  new: { color: 'error', icon: <ErrorIcon />, label: 'New' },
  acknowledged: { color: 'warning', icon: <Schedule />, label: 'Acknowledged' },
  auto_resolved: { color: 'info', icon: <Visibility />, label: 'Needs Review' },
  resolved: { color: 'success', icon: <CheckCircleOutline />, label: 'Resolved' },
  dismissed: { color: 'success', icon: <CheckCircleOutline />, label: 'Dismissed' },
  promoted: { color: 'info', icon: <OpenInNew />, label: 'Promoted' },
};

export interface StatusChipProps extends Omit<ChipProps, 'color' | 'icon' | 'label'> {
  /**
   * Status value to display
   */
  status: ServiceStatus | AlertStatus | string;
  /**
   * Type of status being displayed
   */
  type?: 'service' | 'alert';
  /**
   * Custom label override
   */
  label?: string;
  /**
   * Whether to show the status icon
   */
  showIcon?: boolean;
}

/**
 * Reusable chip component for displaying status values.
 * Supports both service status (up/down/unknown) and alert status (new/acknowledged/dismissed/promoted).
 *
 * @example
 * // Service status
 * <StatusChip status="up" type="service" />
 *
 * @example
 * // Alert status
 * <StatusChip status="acknowledged" type="alert" />
 *
 * @example
 * // Without icon
 * <StatusChip status="down" showIcon={false} />
 */
export function StatusChip({
  status,
  type = 'service',
  label: customLabel,
  showIcon = true,
  size = 'small',
  variant,
  sx,
  ...props
}: StatusChipProps) {
  const config = type === 'alert'
    ? ALERT_STATUS_CONFIG[status as AlertStatus]
    : SERVICE_STATUS_CONFIG[status as ServiceStatus];

  // Fallback for unknown status values
  const fallbackConfig: StatusConfig = {
    color: 'default',
    icon: <HelpIcon />,
    label: String(status).charAt(0).toUpperCase() + String(status).slice(1),
  };

  const { color, icon, label: defaultLabel } = config || fallbackConfig;
  const displayLabel = customLabel || defaultLabel;

  return (
    <Chip
      size={size}
      color={color}
      icon={showIcon ? icon : undefined}
      label={displayLabel}
      variant={variant}
      sx={{
        textTransform: 'capitalize',
        ...sx,
      }}
      {...props}
    />
  );
}

export default StatusChip;
